import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, ChevronDown, Menu, Home, Share2, Film } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { useLang } from "../../context/LanguageContext";

const ITEMS = [
  { key: "nav_how", lkey: "set_it_how_l", dkey: "set_it_how_d" },
  { key: "nav_challenge", lkey: "set_it_challenge_l", dkey: "set_it_challenge_d" },
  { key: "nav_gallery", lkey: "set_it_gallery_l", dkey: "set_it_gallery_d" },
];

const HOME_ITEMS = [
  { key: "home_gallery_teaser", lkey: "set_it_teaser_l", dkey: "set_it_teaser_d" },
];

const BLOG_ITEMS = [
  { key: "nav_blog", lkey: "Le Blog (menu public)", dkey: "Afficher le lien « Blog » dans le menu du site public." },
];

const WM_POSITIONS = [
  { v: "haut-droit", label: "Haut droit" },
  { v: "haut-gauche", label: "Haut gauche" },
  { v: "bas-droit", label: "Bas droit" },
  { v: "bas-gauche", label: "Bas gauche" },
];

const SOCIALS = [
  { key: "facebook_url", label: "Facebook" },
  { key: "instagram_url", label: "Instagram" },
  { key: "linkedin_url", label: "LinkedIn" },
  { key: "x_url", label: "X (Twitter)" },
  { key: "youtube_url", label: "YouTube" },
  { key: "tiktok_url", label: "TikTok" },
];

export default function AdminSettings() {
  const { settings: ctx, setSettings, refresh } = useSettings();
  const { tf: t } = useLang();
  const [local, setLocal] = useState(null);
  const [socialSaving, setSocialSaving] = useState(false);
  const [wmSaving, setWmSaving] = useState(false);

  useEffect(() => {
    api.get("/public/settings").then((r) => setLocal(r.data)).catch(() => setLocal(ctx));
  }, []);
  if (!local) return <div className="py-20 text-center text-ink-secondary">{t("adm_loading")}</div>;

  const toggle = async (key) => {
    const next = { ...local, [key]: !local[key] };
    setLocal(next);
    const it = [...ITEMS, ...HOME_ITEMS, ...BLOG_ITEMS].find((i) => i.key === key);
    try {
      const { data } = await api.put("/admin/settings", { [key]: !local[key] });
      setLocal(data);
      setSettings(data);
      refresh();
      toast.success(`« ${t(it.lkey)} » ${data[key] ? t("set_activated") : t("set_deactivated")}.`);
    } catch (e) {
      setLocal(local);
      toast.error(apiErr(e));
    }
  };

  const saveWatermark = async () => {
    setWmSaving(true);
    try {
      const { data } = await api.put("/admin/settings", {
        watermark_position: local.watermark_position || "haut-droit",
        watermark_size: parseInt(local.watermark_size, 10) || 160,
        watermark_opacity: parseFloat(local.watermark_opacity) || 0.3,
      });
      setLocal(data); setSettings(data); refresh();
      toast.success("Réglages du filigrane enregistrés (appliqués aux prochaines vidéos).");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setWmSaving(false); }
  };

  const saveSocials = async () => {
    setSocialSaving(true);
    try {
      const payload = Object.fromEntries(SOCIALS.map((s) => [s.key, local[s.key] || ""]));
      const { data } = await api.put("/admin/settings", payload);
      setLocal(data); setSettings(data); refresh();
      toast.success("Liens réseaux sociaux enregistrés.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSocialSaving(false); }
  };

  const renderRow = (it, i) => {
    const on = local[it.key];
    return (
      <motion.div key={it.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
        className="flex items-center justify-between gap-4 border border-white/10 bg-surface p-5" data-testid={`setting-row-${it.key}`}>
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 ${on ? "text-cyan" : "text-ink-tertiary"}`}>{on ? <Eye size={18} /> : <EyeOff size={18} />}</span>
          <div>
            <div className="font-display text-lg font-600 text-white">{t(it.lkey)}</div>
            <div className="mt-1 text-sm text-ink-secondary">{t(it.dkey)}</div>
            <div className={`mt-2 text-[11px] uppercase tracking-wider ${on ? "text-cyan" : "text-ink-tertiary"}`}>{on ? t("set_on") : t("set_off")}</div>
          </div>
        </div>
        <button
          onClick={() => toggle(it.key)} data-testid={`setting-toggle-${it.key}`} role="switch" aria-checked={on}
          className={`relative h-7 w-12 flex-shrink-0 border transition-colors ${on ? "border-cyan bg-cyan" : "border-white/20 bg-elevated"}`}
        >
          <span className={`absolute top-0.5 h-5 w-5 transition-all ${on ? "left-6 bg-void" : "left-0.5 bg-ink-secondary"}`} />
        </button>
      </motion.div>
    );
  };

  const TABS = [
    { key: "menu", label: "Menu public", icon: Menu, color: "cyan" },
    { key: "accueil", label: "Page d'accueil", icon: Home, color: "green" },
    { key: "blog", label: "Blog & réseaux sociaux", icon: Share2, color: "amber" },
    { key: "filigrane", label: "Vidéos IA (filigrane)", icon: Film, color: "rose" },
  ];
  const TAB_COLORS = {
    cyan: { active: "border-cyan bg-cyan/15 text-cyan", idle: "border-cyan/30 bg-cyan/5 text-cyan/80 hover:border-cyan hover:text-cyan" },
    green: { active: "border-green-400 bg-green-400/15 text-green-400", idle: "border-green-400/30 bg-green-400/5 text-green-400/80 hover:border-green-400 hover:text-green-400" },
    amber: { active: "border-amber-400 bg-amber-400/15 text-amber-300", idle: "border-amber-400/30 bg-amber-400/5 text-amber-300/80 hover:border-amber-400 hover:text-amber-300" },
    rose: { active: "border-rose-400 bg-rose-400/15 text-rose-300", idle: "border-rose-400/30 bg-rose-400/5 text-rose-300/80 hover:border-rose-400 hover:text-rose-300" },
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("set_kicker")}</div>
      <h1 className="mt-3 font-display text-3xl font-900 text-white">{t("set_title")}</h1>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-ink-secondary">
        {t("set_desc")}
      </p>

      {/* Onglets encadrés et colorés des réglages (clic = ouvrir, reclic = fermer) */}

      {/* Section « Menu public » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-menu">Menu public</div>
      <div className="mt-8" data-testid="settings-panel-menu">
        <div className="space-y-4">
          {ITEMS.map((it, i) => renderRow(it, i))}
        </div>
      </div>
      </>

      {/* Section « Page d'accueil » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-accueil">Page d'accueil</div>
      <div className="mt-8" data-testid="settings-panel-accueil">
        <p className="max-w-xl text-sm leading-relaxed text-ink-secondary">{t("set_home_desc")}</p>
        <div className="mt-6 space-y-4">
          {HOME_ITEMS.map((it, i) => renderRow(it, i))}
        </div>
      </div>
      </>

      {/* Section « Blog & réseaux sociaux » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-blog">Blog & réseaux sociaux</div>
      <div className="mt-8" data-testid="settings-panel-blog">
        <p className="max-w-xl text-sm leading-relaxed text-ink-secondary">
          Activez le blog dans le menu public et renseignez les liens de vos réseaux sociaux
          (affichés sur le blog pour permettre aux visiteurs de vous suivre).
        </p>
        <div className="mt-6 space-y-4">
          {BLOG_ITEMS.map((it, i) => renderRow(it, i))}
        </div>

        <div className="mt-6 border border-white/10 bg-surface p-5" data-testid="social-links-editor">
          <div className="grid gap-4 sm:grid-cols-2">
            {SOCIALS.map((s) => (
              <label key={s.key} className="block">
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">{s.label}</span>
                <input
                  type="url" value={local[s.key] || ""} onChange={(e) => setLocal({ ...local, [s.key]: e.target.value })}
                  placeholder="https://…" data-testid={`social-${s.key}`}
                  className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
              </label>
            ))}
          </div>
          <div className="mt-5 flex justify-end">
            <button onClick={saveSocials} disabled={socialSaving} data-testid="social-save"
              className="bg-cyan px-6 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60">
              {socialSaving ? "Enregistrement…" : "Enregistrer les liens"}
            </button>
          </div>
        </div>
      </div>
      </>

      {/* Section « Vidéos IA (filigrane) » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-filigrane">Vidéos IA (filigrane)</div>
      <div className="mt-8" data-testid="settings-panel-filigrane">
        <p className="max-w-xl text-sm leading-relaxed text-ink-secondary">
          Réglez l'apparence du logo en filigrane sur les vidéos. Ces réglages s'appliquent aux prochaines
          vidéos générées (utilisez « Régénérer la vidéo » pour les vidéos existantes). Format : 16:9 (horizontal).
        </p>

        <div className="mt-6 border border-white/10 bg-surface p-5" data-testid="watermark-editor">
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Position du logo</span>
              <select value={local.watermark_position || "haut-droit"} onChange={(e) => setLocal({ ...local, watermark_position: e.target.value })}
                data-testid="wm-position"
                className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm text-white outline-none focus:border-cyan [color-scheme:dark]">
                {WM_POSITIONS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Taille du logo (px)</span>
              <input type="number" min="40" max="600" value={local.watermark_size ?? 160} onChange={(e) => setLocal({ ...local, watermark_size: e.target.value })}
                data-testid="wm-size"
                className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm text-white outline-none focus:border-cyan" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Opacité : {Math.round((local.watermark_opacity ?? 0.3) * 100)}%</span>
              <input type="range" min="0.05" max="1" step="0.05" value={local.watermark_opacity ?? 0.3} onChange={(e) => setLocal({ ...local, watermark_opacity: e.target.value })}
                data-testid="wm-opacity" className="mt-4 w-full accent-cyan" />
            </label>
          </div>
          <div className="mt-5 flex justify-end">
            <button onClick={saveWatermark} disabled={wmSaving} data-testid="wm-save"
              className="bg-cyan px-6 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60">
              {wmSaving ? "Enregistrement…" : "Enregistrer le filigrane"}
            </button>
          </div>
        </div>
      </div>
      </>
    </div>
  );
}
